import type { Message } from "ably"
import type { ReactNode } from "react"
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react"
import { useAuthStore } from "@/features/auth/store/authStore"
import { type RealtimeConnectionStatus, useAblyRealtimeClient } from "@/hooks/useAblyRealtimeClient"

export interface RealtimeContextValue {
  isConnected: boolean
  connectionStatus: RealtimeConnectionStatus
  transport: "ably"
  subscribeToChannel: (channelName: string, handler: (message: Message) => void) => () => void
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null)
const REALTIME_TOKEN_URL = `${import.meta.env.VITE_API_URL}/api/realtime/token`

export function RealtimeProvider({ children }: { children: ReactNode }) {
  const user = useAuthStore(state => state.user)

  const { client, isConnected, connectionStatus } = useAblyRealtimeClient({
    userId: user?.id ?? null,
    tokenUrl: REALTIME_TOKEN_URL,
  })

  const subscribeToChannel = useCallback(
    (channelName: string, handler: (message: Message) => void) => {
      if (!client) {
        return () => {}
      }

      const channel = client.channels.get(channelName)
      const channelHandler = (message: Message) => handler(message)

      channel.subscribe(channelHandler).catch(error => {
        console.error("Failed to subscribe to realtime channel", {
          channelName,
          error: error instanceof Error ? error.message : String(error),
        })
      })

      return () => {
        channel.unsubscribe(channelHandler)
      }
    },
    [client],
  )

  const value = useMemo<RealtimeContextValue>(
    () => ({
      isConnected,
      connectionStatus,
      transport: "ably",
      subscribeToChannel,
    }),
    [connectionStatus, isConnected, subscribeToChannel],
  )

  return <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
}

export function useRealtimeConnection() {
  const context = useContext(RealtimeContext)

  if (!context) {
    throw new Error("useRealtimeConnection must be used within RealtimeProvider")
  }

  return {
    isConnected: context.isConnected,
    connectionStatus: context.connectionStatus,
    transport: context.transport,
  }
}

export function useRealtimeEvent(eventName: string, callback: (...args: unknown[]) => void): void {
  const userId = useAuthStore(state => state.user?.id)
  useRealtimeChannelEvent(userId ? `user:${userId}` : null, eventName, callback)
}

export function useRealtimeChannelMessages(
  channelName: string | null,
  callback: (message: Message) => void,
): void {
  const context = useContext(RealtimeContext)
  if (!context) {
    throw new Error("useRealtimeChannelMessages must be used within RealtimeProvider")
  }

  const callbackRef = useRef(callback)
  const { subscribeToChannel } = context

  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  useEffect(() => {
    if (!channelName) {
      return () => {}
    }

    return subscribeToChannel(channelName, message => callbackRef.current(message))
  }, [channelName, subscribeToChannel])
}

export function useRealtimeChannelEvent(
  channelName: string | null,
  eventName: string,
  callback: (...args: unknown[]) => void,
): void {
  useRealtimeChannelMessages(channelName, message => {
    if (message.name === eventName) {
      callback(message.data)
    }
  })
}
